<script setup lang="ts">
import { Stamp } from "@element-plus/icons-vue";
import { computed, inject, onMounted, ref } from "vue";
import {
  ActionButtonType,
  FormElementType,
  NameStampAnswerValue,
  NameStampFormElement,
} from "~/api";
import {
  type WorkflowState,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { apiClient } from "~/utils/apiClient";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
const myInfoStore = useMyInfoStore();

const props = defineProps<DefaultFormComponentProps<NameStampFormElement>>();

onMounted(async () => {
  await myInfoStore.load();
});

const {
  useAnswer,
  formResponse,
  updateFormResponseStatus,
  inFlightRequestMap,
  substituteTemplatedText,
  getUnansweredQuestions,
  flushPendingAnswerSaves,
} = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const inPdf = useIsInPdfMode();

/**
 * Feature flag: controls whether an unstamped NameStamp renders blank signature lines
 * in PDF / print output (for scenarios where the paper form needs to be signed by hand).
 *
 * - false (current): inPdf strictly reflects the persisted digital state.
 *   If no stamp has been applied, no signature block is shown (matches the "Stamp button" case).
 * - true: restores the previous behavior of showing "Signature:" / "Date:" lines when !localAnswer.length.
 *
 * This preserves the paper-signable printout capability without re-enabling it for the current scenario.
 */
const showBlankSignatureInPdf = false;

const { updateWorkflowStatusFromNameStamp } = inject(workflowInjectKey, {
  updateWorkflowStatus: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  updateWorkflowStatusFromNameStamp: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  getWorkflowStatus: () => {
    return "";
  },
  getWorkflow: () => {
    return undefined;
  },
}) as WorkflowState;

const answer = useAnswer<FormElementType.NAME_STAMP>(
  props.modelValue,
  (newVal) =>
    Promise.resolve({
      status: "updated",
      value: newVal ?? [],
    }),
);
const localAnswer = ref<NameStampAnswerValue[]>(answer.value ?? []);

const currentStamp = computed<NameStampAnswerValue | undefined>(
  () => localAnswer.value?.find((s) => !s.revoked) ?? undefined,
);

const cannotClick = computed<boolean>(() => {
  if (!currentStamp.value || props.modelValue.properties.allowMultipleClicks)
    return false;
  // Allow re-clicking if the status was reverted (e.g. by AnswerReview
  // detecting an unanswered question after the stamp was applied)
  const targetStatus = props.modelValue.properties.changeToStatus;
  if (targetStatus && formResponse && formResponse.status !== targetStatus)
    return false;
  return true;
});

const isRequestInFlight = computed<boolean>(
  () => inFlightRequestMap.value.size > 0,
);

const hasUnansweredQuestions = computed<boolean>(() => {
  if (!props.modelValue.properties.disableIfUnansweredQuestions) return false;
  return getUnansweredQuestions().length > 0;
});

const { getWorkflow } = inject(workflowInjectKey, {
  updateWorkflowStatus: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  updateWorkflowStatusFromNameStamp: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  getWorkflowStatus: () => {
    return "";
  },
  getWorkflow: () => {
    return undefined;
  },
}) as WorkflowState;

const pdfLoadingStore = usePdfLoadingStore();

const workflowGuid = computed(
  () => getWorkflow()?.guid || pdfLoadingStore.workflowGuid || undefined,
);

const makePdf = async (pdfCode?: string) => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }

  // Ensure we pass the ConnectionId for the "self" hub (where PdfMsg listener is).
  await hubManager.getHub("self", "/self");
  const selfConnId =
    hubManager.getHubConnectionId("self") ?? hubManager.ConnectionId ?? "";
  await pdfLoadingStore.makePdf(pdfCode, workflowGuid.value, selfConnId);
};

const sendEmail = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  const eventCode = props.modelValue.properties.code;
  if (!eventCode) {
    showError("Event code is missing.");
    return;
  }
  await apiClient.workflowInstance.handleSendEmailEvent(
    workflowGuid.value,
    eventCode,
  );
};

const handleAddStamp = async () => {
  if (!formResponse) {
    showError("No stamping allowed on undefined form response");
    return;
  }
  const stamps = await apiClient.nameStampAnswer.addStamp(
    formResponse.guid,
    props.modelValue.guid,
  );

  answer.value = stamps;
  localAnswer.value = stamps;

  if (props.modelValue.properties.changeToStatus) {
    updateFormResponseStatus(props.modelValue.properties.changeToStatus);
  }

  if (props.modelValue.properties.changeWorkflowStatusTo) {
    updateWorkflowStatusFromNameStamp(
      props.modelValue.properties.changeWorkflowStatusTo,
    );
  }

  if (props.modelValue.properties.actionToTake === ActionButtonType.GET_PDF) {
    if (props.modelValue.properties.code) {
      await makePdf(props.modelValue.properties.code);
    } else {
      await makePdf();
    }
  } else if (
    props.modelValue.properties.actionToTake === ActionButtonType.SEND_EMAIL
  ) {
    await sendEmail();
  } else if (
    props.modelValue.properties.actionToTake ===
    ActionButtonType.RUN_PEOPLE_MIGRATION
  ) {
    await runPeopleMigration();
  } else if (
    props.modelValue.properties.actionToTake !== ActionButtonType.NONE &&
    props.modelValue.properties.actionToTake !== null &&
    props.modelValue.properties.actionToTake !== undefined
  ) {
    // May have other action types in the future
    showError(
      "Unsupported action type:" + props.modelValue.properties.actionToTake,
    );
  }
};

const handleRevokeStamp = async (stamp?: NameStampAnswerValue) => {
  if (!formResponse) {
    showError(
      "Developer Error: No stamping allowed on undefined form response",
    );
    return;
  } else if (!stamp) {
    showError("Developer Error: Tried to revoke an undefined stamp");
    return;
  }
  const stamps = await apiClient.nameStampAnswer.removeStamp(
    formResponse.guid,
    props.modelValue.guid,
    stamp.guid,
  );

  answer.value = stamps;
  localAnswer.value = stamps;

  if (props.modelValue.properties.changeBackToStatus) {
    updateFormResponseStatus(props.modelValue.properties.changeBackToStatus);
  }

  if (props.modelValue.properties.changeWorkflowStatusBackTo) {
    updateWorkflowStatusFromNameStamp(
      props.modelValue.properties.changeWorkflowStatusBackTo,
    );
  }
};

const runPeopleMigration = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  // Migration re-reads the saved answers server-side, so make sure any pending
  // debounced saves have persisted first — otherwise it reads stale values.
  await flushPendingAnswerSaves();
  try {
    const result = await apiClient.workflowInstance.handleRunPeopleMigration(
      workflowGuid.value,
    );
    console.log(
      `People migration complete: ${result.peopleCreated} created, ${result.skipped} skipped`,
    );
  } catch (e) {
    console.error("People migration failed.", e);
  }
};
</script>

<template>
  <div
    class="NameStampComponent"
    :class="{ 'left-aligned': modelValue.properties.leftAligned, inPdf: inPdf }"
  >
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>
    <div class="Inner">
      <div v-if="!inPdf" class="StampButtons">
        <el-button
          class="signButton"
          :icon="Stamp"
          :disabled="
            isReadOnly ||
            cannotClick ||
            isRequestInFlight ||
            hasUnansweredQuestions
          "
          @click="handleAddStamp"
          >{{
            modelValue.properties.nameStampButtonText?.length === 0
              ? "Approve"
              : substituteTemplatedText(
                  modelValue.properties.nameStampButtonText,
                )
          }}</el-button
        >

        <el-button
          v-if="modelValue.properties.isRevokable"
          class="signButton"
          type="info"
          :disabled="
            isReadOnly ||
            isRequestInFlight ||
            !(
              currentStamp &&
              (myInfoStore.userInfo.userGuid === currentStamp.userGuid ||
                modelValue.properties.isRevokableByAnyUser) &&
              modelValue.properties.isRevokable
            )
          "
          @click="() => handleRevokeStamp(currentStamp)"
          >{{
            modelValue.properties.nameStampRevokeButtonText?.length === 0
              ? "Cancel"
              : substituteTemplatedText(
                  modelValue.properties.nameStampRevokeButtonText,
                )
          }}</el-button
        >
      </div>
      <div
        v-if="inPdf && !localAnswer.length && showBlankSignatureInPdf"
        class="pdf-blank"
      >
        <div class="blankTop">
          <div></div>
          <div>
            {{
              (modelValue.properties.nameStampButtonText?.length === 0
                ? "Approve"
                : substituteTemplatedText(
                    modelValue.properties.nameStampButtonText,
                  )) + ":"
            }}
          </div>
        </div>
        <div class="blank">
          <div>Signature:</div>
          <div></div>
        </div>
        <div class="blank">
          <div>Date:</div>
          <div></div>
        </div>
      </div>
      <div class="existingStamps">
        <template v-for="stamp in localAnswer" :key="stamp.guid">
          <el-tooltip v-if="!inPdf" placement="left" class="stamp">
            <template #content>
              <p :class="{ revoked: stamp.revoked }">
                Clicked by {{ stamp.userName }}
                {{ stamp.emailAddress ? `(${stamp.emailAddress})` : "" }}
              </p>
              <p v-if="stamp.revoked">
                Revoked
                <span v-if="stamp.timeRevoked">
                  on {{ formatDate(stamp.timeRevoked, 5) }}
                </span>
              </p>
            </template>
            <el-tag :class="{ revoked: stamp.revoked }" type="info">
              <div class="stampInner">
                <el-icon>
                  <el-icon-user />
                </el-icon>
                <span>
                  <strong> {{ stamp.userName }}</strong>
                  &nbsp;
                  {{ formatDate(stamp.timeStamped, 5) }}
                  <span v-if="stamp.timeRevoked">
                    <br />
                    Revoked {{ formatDate(stamp.timeRevoked, 5) }}
                  </span>
                </span>
              </div>
            </el-tag>
          </el-tooltip>
          <div v-else-if="inPdf && stamp.revoked" class="revoked forPdf">
            {{ stamp.userName }}
            <span>
              {{ stamp.emailAddress ? ` (${stamp.emailAddress})` : "" }}
            </span>
            <div>Signed online: {{ formatDate(stamp.timeStamped, 5) }}</div>
            <div>Revoked: {{ formatDate(stamp.timeRevoked, 5) }}</div>
          </div>
          <table
            v-else-if="inPdf && !stamp.revoked"
            class="pdf-blank-table forPdf"
          >
            <tbody>
              <tr class="blankTop">
                <th></th>
                <td>
                  {{
                    (modelValue.properties.nameStampButtonText?.length === 0
                      ? "Approve"
                      : substituteTemplatedText(
                          modelValue.properties.nameStampButtonText,
                        )) + ":"
                  }}
                </td>
              </tr>
              <tr class="blank">
                <th>Signed Online By:</th>
                <td>
                  <strong> {{ stamp.userName }}</strong>
                  <span>
                    {{ stamp.emailAddress ? ` (${stamp.emailAddress})` : "" }}
                  </span>
                </td>
              </tr>
              <tr class="blank">
                <th>Date:</th>
                <td>
                  {{ formatDate(stamp.timeStamped, 5) }}
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.NameStampComponent {
  display: flex;
  gap: 10px;

  align-items: baseline;

  .Inner {
    display: flex;
    flex-direction: column;
    .StampButtons {
      display: flex;
      justify-content: flex-end;
    }
  }

  &.left-aligned {
    flex-direction: column;
    .StampButtons {
      display: flex;
      justify-content: flex-start;
    }
  }

  .signButton {
    width: min-content;
    margin-bottom: 8px;
  }
  .existingStamps {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .revoked {
      text-decoration: line-through;
    }

    .stamp {
      font-size: 0.8em;
      place-self: flex-end;
    }
    .stampInner {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .forPdf {
    &.revoked {
      text-align: right;
      border: 1px solid #ccc;
      width: fit-content;
      margin: 0 0 0 auto;
      padding: 0.25em 0.5em;
      border-radius: 3px;
    }
  }
  .pdf-blank {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1.75em;
    .blank,
    .blankTop {
      display: flex;
      div:last-child {
        min-width: 18rem;
        margin-left: 0.5rem;
      }
    }
    .blank {
      div:last-child {
        border-bottom: 1px solid #666;
      }
    }
    .blankTop {
      div:last-child {
        font-weight: bold;
        padding-left: 0.5em;
        padding-bottom: 0;
      }
    }
  }
  .pdf-blank-table {
    th {
      font-weight: normal;
      text-align: right;
      padding: 0.5em 0.5em 0 0;
    }
    td {
      padding: 0.5em 2em 0 2em;
    }
    .blank,
    .blankTop {
      td {
        min-width: 18rem;
        padding-left: 0.5rem;
      }
      td:last-child {
        width: 60%;
      }
    }
    .blank {
      td {
        border-bottom: 1px solid #666;
        font-size: var(--el-font-size-base);
      }
      td:last-child {
        white-space: nowrap;
      }
    }
    .blankTop {
      td {
        font-weight: bold;
        padding-left: 0.5em;
        padding-bottom: 0;
      }
    }
  }
}
</style>
